/**
 * The review queue: merge proposals and risk hypotheses, both waiting for a person.
 *
 * **The feature table is not optional.** ADR-0008 requires a proposal to be
 * explainable, and `MergeProposal` refuses to exist without features for exactly that
 * reason. Here the requirement becomes a layout rule: the evidence *against* a merge is
 * shown as prominently as the evidence for it, in the same table, above the buttons. A
 * review interface presenting only supporting evidence is a rubber stamp with extra
 * steps.
 *
 * There is no bulk approve. If the queue is too long, the answer is better blocking or
 * better weights, not a button that decides many things at once.
 */

import { useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '@/lib/api';
import type { Hypothesis, HypothesisReviewRequest, MergeProposal, ReviewDecisionRequest } from '@/lib/schemas';
import { Badge, Button, Card, ErrorState, Loading, ProvenanceLink, Table } from '@/components/ui';
import { DecisionLog } from '@/features/review/DecisionLog';
import { useCan } from '@/hooks/usePermissions';

/** The review queue for one case. */
export function ReviewQueue({ caseId }: { caseId: string }): ReactElement {
  const proposals = useQuery({
    queryKey: ['proposals', caseId],
    queryFn: () =>
      request<MergeProposal[]>(`/cases/${caseId}/merge-proposals`, {
        query: { status: 'pending' },
      }),
  });
  const hypotheses = useQuery({
    queryKey: ['hypotheses', caseId],
    queryFn: () =>
      request<Hypothesis[]>(`/cases/${caseId}/hypotheses`, { query: { status: 'unconfirmed' } }),
  });

  if (proposals.isLoading || hypotheses.isLoading) return <Loading />;
  if (proposals.error) return <ErrorState error={proposals.error} />;
  if (hypotheses.error) return <ErrorState error={hypotheses.error} />;

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <h2 className="text-sm font-semibold">
          Merge proposals ({proposals.data?.length ?? 0})
        </h2>
        {(proposals.data ?? []).length === 0 ? (
          <p className="text-sm text-muted">Nothing is waiting on a merge decision.</p>
        ) : (
          (proposals.data ?? []).map((proposal) => (
            <ProposalCard key={proposal.id} caseId={caseId} proposal={proposal} />
          ))
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold">
          Risk hypotheses ({hypotheses.data?.length ?? 0})
        </h2>
        {(hypotheses.data ?? []).length === 0 ? (
          <p className="text-sm text-muted">Nothing is waiting on a hypothesis decision.</p>
        ) : (
          (hypotheses.data ?? []).map((hypothesis) => (
            <HypothesisCard key={hypothesis.id} caseId={caseId} hypothesis={hypothesis} />
          ))
        )}
      </section>

      {/* What was already decided. Shipped since M4 with nothing displaying it, so "why
          are these two the same person?" could only be answered by whoever remembered. */}
      <section className="space-y-3">
        <DecisionLog caseId={caseId} />
      </section>
    </div>
  );
}

/** One proposal, with its feature table and its conflicts. */
function ProposalCard({
  caseId,
  proposal,
}: {
  caseId: string;
  proposal: MergeProposal;
}): ReactElement {
  const canApprove = useCan('approve_merges');
  const queryClient = useQueryClient();
  const [rationale, setRationale] = useState('');

  const decide = useMutation({
    mutationFn: (decision: 'approve' | 'reject' | 'defer') =>
      request<unknown, ReviewDecisionRequest>(`/merge-proposals/${proposal.id}:decide`, {
        method: 'POST',
        body: { decision, rationale },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['proposals', caseId] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  return (
    <Card
      title={`${proposal.left_label}  ⇄  ${proposal.right_label}`}
      action={
        <span className="font-mono text-xs text-muted">
          {proposal.score.toFixed(3)} · scorer {proposal.scorer_version}
        </span>
      }
    >
      <div className="space-y-4">
        <div>
          <h3 className="mb-1 text-xs uppercase tracking-wide text-muted">
            Why the scorer proposed this
          </h3>
          <Table
            headers={['Feature', 'Value', 'Weight', 'Detail']}
            empty="No features — this proposal should not exist."
            rows={proposal.features.map((feature) => [
              <span key="n" className="font-mono text-xs">
                {feature.name}
              </span>,
              <span key="v" className="font-mono text-xs">
                {feature.value.toFixed(2)}
              </span>,
              <span key="w" className="font-mono text-xs text-muted">
                {feature.weight.toFixed(2)}
              </span>,
              <span key="d" className="text-xs text-muted">
                {feature.detail}
              </span>,
            ])}
          />
        </div>

        <div>
          <h3 className="mb-1 text-xs uppercase tracking-wide text-danger">
            Evidence against this merge
          </h3>
          {proposal.conflicts.length === 0 ? (
            <p className="text-xs text-muted">
              No conflicting claims found. That is not evidence for the merge.
            </p>
          ) : (
            <Table
              headers={['Conflict', 'Left', 'Right', 'Weight', 'Detail']}
              rows={proposal.conflicts.map((conflict) => [
                <span key="n" className="font-mono text-xs">
                  {conflict.name}
                </span>,
                // Read from the stored conflict, not from the detail sentence. A
                // proposal written before the scorer carried the sides shows an em dash
                // here and still says everything in `Detail`.
                <span key="l" className="text-xs">
                  {conflict.left === '' ? '—' : conflict.left}
                </span>,
                <span key="r" className="text-xs">
                  {conflict.right === '' ? '—' : conflict.right}
                </span>,
                <span key="w" className="font-mono text-xs text-danger">
                  −{conflict.weight.toFixed(2)}
                </span>,
                <span key="d" className="text-xs text-muted">
                  {conflict.detail}
                </span>,
              ])}
            />
          )}
        </div>

        <label className="block text-sm">
          <span className="text-muted">Rationale</span>
          <textarea
            rows={2}
            value={rationale}
            onChange={(event) => setRationale(event.target.value)}
            className="mt-1 w-full rounded border border-line bg-surface px-2 py-1.5 text-sm"
          />
        </label>

        {decide.error != null && <ErrorState error={decide.error} />}

        {canApprove ? (
          <div className="flex gap-2">
            <Button tone="primary" disabled={decide.isPending} onClick={() => decide.mutate('approve')}>
              Approve merge
            </Button>
            <Button tone="danger" disabled={decide.isPending} onClick={() => decide.mutate('reject')}>
              Reject permanently
            </Button>
            <Button disabled={decide.isPending} onClick={() => decide.mutate('defer')}>
              Defer
            </Button>
          </div>
        ) : (
          <p className="text-xs text-muted">
            Deciding a merge requires the <code>approve_merges</code> permission, which your
            role does not hold.
          </p>
        )}
      </div>
    </Card>
  );
}

/** One hypothesis, with the evidence it rests on. */
function HypothesisCard({
  caseId,
  hypothesis,
}: {
  caseId: string;
  hypothesis: Hypothesis;
}): ReactElement {
  const canConfirm = useCan('confirm_hypotheses');
  const queryClient = useQueryClient();
  const [rationale, setRationale] = useState('');

  // The verb is written out rather than interpolated. `/hypotheses/${id}:${action}` is a
  // path no static check can resolve, so a typo in either verb would have reached a user
  // as a 404 — which is exactly how every evidence link came to be broken.
  const decide = useMutation({
    mutationFn: (action: 'confirm' | 'dismiss') =>
      action === 'confirm'
        ? request<unknown, HypothesisReviewRequest>(`/hypotheses/${hypothesis.id}:confirm`, {
            method: 'POST',
            body: { rationale },
          })
        : request<unknown, HypothesisReviewRequest>(`/hypotheses/${hypothesis.id}:dismiss`, {
            method: 'POST',
            body: { rationale },
          }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['hypotheses', caseId] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  return (
    <Card
      title={hypothesis.statement}
      action={
        <span className="flex gap-2">
          <Badge tone="muted">{hypothesis.kind}</Badge>
          <Badge tone={hypothesis.severity === 'high' ? 'danger' : 'warn'}>
            {hypothesis.severity}
          </Badge>
        </span>
      }
    >
      <div className="space-y-3">
        <p className="text-xs text-muted">
          Unconfirmed. Nothing here can appear in a report until a person confirms it.
        </p>
        <div className="flex flex-wrap gap-1">
          {hypothesis.cited_assertion_ids.map((id) => (
            <ProvenanceLink key={id} caseId={caseId} assertionId={id} />
          ))}
        </div>
        <label className="block text-sm">
          <span className="text-muted">Rationale — required to confirm</span>
          <textarea
            rows={2}
            value={rationale}
            onChange={(event) => setRationale(event.target.value)}
            className="mt-1 w-full rounded border border-line bg-surface px-2 py-1.5 text-sm"
          />
        </label>
        {decide.error != null && <ErrorState error={decide.error} />}
        {canConfirm ? (
          <div className="flex gap-2">
            <Button
              tone="primary"
              disabled={decide.isPending || rationale.trim().length === 0}
              onClick={() => decide.mutate('confirm')}
            >
              Confirm
            </Button>
            <Button tone="danger" disabled={decide.isPending} onClick={() => decide.mutate('dismiss')}>
              Dismiss
            </Button>
          </div>
        ) : (
          <p className="text-xs text-muted">
            Confirming requires the <code>confirm_hypotheses</code> permission, which your role
            does not hold.
          </p>
        )}
      </div>
    </Card>
  );
}
